import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

import { MainLayout } from '../components/Layout';
const QUALITY_MAP = {
  BOA: { icon: '\u{1F7E2}', label: 'Boa', cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700' },
  MEDIA: { icon: '\u{1F7E1}', label: 'Media', cls: 'bg-yellow-900/40 text-yellow-300 border-yellow-700' },
  RUIM: { icon: '\u{1F534}', label: 'Ruim', cls: 'bg-red-900/40 text-red-300 border-red-700' },
};

const STATUS_MAP = {
  VENDA: { label: 'Venda', cls: 'bg-emerald-800 text-emerald-200' },
  ABANDONO: { label: 'Abandono', cls: 'bg-red-800 text-red-200' },
  SUPORTE: { label: 'Suporte', cls: 'bg-blue-800 text-blue-200' },
  EM_ANDAMENTO: { label: 'Em andamento', cls: 'bg-gray-700 text-gray-200' },
};

function KPICard({ title, value, subtitle, color }) {
  return (
    <div data-testid={`kpi-${title.toLowerCase().replace(/\s/g,'-')}`} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
      <p className="text-xs text-gray-400 uppercase tracking-wide">{title}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-white'}`}>{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function QualityBadge({ quality }) {
  const q = QUALITY_MAP[quality] || QUALITY_MAP.MEDIA;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${q.cls}`}>{q.icon} {q.label}</span>;
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.EM_ANDAMENTO;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function BarChart({ data, labelKey, valueKey, maxBarWidth }) {
  if (!data || data.length === 0) return <p className="text-gray-500 text-sm">Sem dados</p>;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0));
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-gray-400 truncate text-right">{d[labelKey]}</span>
          <div className="flex-1 bg-gray-700 rounded h-4 overflow-hidden">
            <div className="bg-orange-500 h-full rounded transition-all"
              style={{ width: `${maxVal > 0 ? (d[valueKey] / maxVal * 100) : 0}%`, maxWidth: maxBarWidth || '100%' }} />
          </div>
          <span className="text-gray-300 w-8">{d[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

function ConversationDetail({ jid, days, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/monitoring/conversations/${encodeURIComponent(jid)}/analysis?days=${days}`);
        setData(res.data);
      } catch { setData(null); }
      setLoading(false);
    })();
  }, [jid, days]);

  if (loading) return <div className="p-8 text-center text-gray-400">Carregando analise...</div>;
  if (!data || !data.messages.length) return <div className="p-8 text-center text-gray-400">Sem dados para esta conversa.</div>;

  const { messages, summary } = data;

  return (
    <div data-testid="conversation-detail" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-white">{summary.contact_name}</h3>
            <p className="text-xs text-gray-400">{summary.total_messages} mensagens | {summary.total_errors} erros | {summary.total_frustrations} frustracoes</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl px-2">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`rounded-lg p-3 ${m.bot_success ? 'bg-gray-800' : 'bg-red-900/30 border border-red-800'}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-400">{new Date(m.timestamp).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${m.intent === 'SAUDACAO' ? 'bg-blue-800 text-blue-200' : m.intent === 'PEDIDO' ? 'bg-emerald-800 text-emerald-200' : m.intent === 'RECLAMACAO' ? 'bg-red-800 text-red-200' : 'bg-gray-700 text-gray-300'}`}>{m.intent}</span>
                {m.frustration && <span className="text-xs text-red-400">Frustracao detectada</span>}
                {m.sale_opportunity && <span className="text-xs text-yellow-400">Oportunidade de venda</span>}
                {m.human_intervention && <span className="text-xs text-blue-400">Humano interveio</span>}
              </div>
              <div className="mb-1">
                <span className="text-xs text-orange-400 font-medium">Cliente:</span>
                <p className="text-sm text-gray-200 ml-2">{m.user_message || '-'}</p>
              </div>
              <div>
                <span className="text-xs text-emerald-400 font-medium">Bot:</span>
                <p className="text-sm text-gray-300 ml-2 whitespace-pre-wrap">{m.bot_response || '-'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MonitoringPage() {
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [insights, setInsights] = useState(null);
  const [totalConvs, setTotalConvs] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [selectedJid, setSelectedJid] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [activeAlerts, setActiveAlerts] = useState({tickets:[], queue:[], counts:{tickets_open:0, tickets_high:0, queue_open:0}});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, convsRes, insightsRes, alertsRes] = await Promise.all([
        api.get(`/monitoring/stats?days=${days}`),
        api.get(`/monitoring/conversations?days=${days}&page=${page}&limit=30${qualityFilter ? `&quality=${qualityFilter}` : ''}${search ? `&search=${search}` : ''}`),
        api.get(`/monitoring/insights?days=${days}`),
        api.get('/monitoring/active-alerts?status_filter=OPEN')
      ]);
      setStats(statsRes.data);
      setConversations(convsRes.data.conversations || []);
      setTotalConvs(convsRes.data.total || 0);
      setInsights(insightsRes.data);
      setActiveAlerts(alertsRes.data || {tickets:[], queue:[], counts:{tickets_open:0, tickets_high:0, queue_open:0}});
    } catch (e) {
      console.error('Erro ao carregar monitoramento:', e);
    }
    setLoading(false);
  }, [days, page, qualityFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const iv = setInterval(() => { if (tab === 'alertas') fetchData(); }, 30000);
    return () => clearInterval(iv);
  }, [tab, fetchData]);

  const scoreColor = (s) => s >= 8 ? 'text-emerald-400' : s >= 5 ? 'text-yellow-400' : 'text-red-400';

  return (

    <MainLayout>
    <div data-testid="monitoring-page" className="min-h-screen bg-gray-950 text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl font-bold">Monitoramento Inteligente</h1>
          <p className="text-sm text-gray-400">Analise automatica de conversas do bot</p>
        </div>
        <div className="flex items-center gap-2">
          {[{l:'Hoje',v:1},{l:'7 dias',v:7},{l:'15 dias',v:15},{l:'30 dias',v:30}].map(p=>(
            <button key={p.v} onClick={()=>{setDays(p.v);setPage(1);}}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${days===p.v?'bg-orange-600 text-white':'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>{p.l}</button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-800">
        {[{k:'dashboard',l:'Dashboard'},{k:'alertas',l:'Alertas'+(activeAlerts.counts.tickets_open+activeAlerts.counts.queue_open>0?' ('+(activeAlerts.counts.tickets_open+activeAlerts.counts.queue_open)+')':'')},{k:'conversas',l:'Conversas'},{k:'insights',l:'Insights'}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 ${tab===t.k?'border-orange-500 text-orange-400':'border-transparent text-gray-400 hover:text-gray-200'}`}>{t.l}</button>
        ))}
      </div>

      {loading && !stats ? (
        <div className="text-center py-12 text-gray-400">Carregando dados...</div>
      ) : (
        <>
          {/* DASHBOARD TAB */}
          {tab === 'dashboard' && stats && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KPICard title="Mensagens" value={stats.total_mensagens} subtitle={`${stats.contatos_unicos} contatos`} />
                <KPICard title="Score do Bot" value={`${stats.bot_score}/10`} color={scoreColor(stats.bot_score)} />
                <KPICard title="Taxa de Erro" value={`${stats.taxa_erro}%`} color={stats.taxa_erro > 20 ? 'text-red-400' : 'text-emerald-400'} />
                <KPICard title="Frustracao" value={`${stats.taxa_frustacao}%`} color={stats.taxa_frustacao > 10 ? 'text-red-400' : 'text-yellow-400'} />
                <KPICard title="Oportunidades" value={stats.oportunidades_venda} subtitle={`${stats.taxa_conversao}% convertidas`} color="text-yellow-400" />
                <KPICard title="Humano" value={stats.intervencoes_humanas} subtitle="intervencoes" color="text-blue-400" />
              </div>

              {/* Quality Distribution + Intents */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Qualidade das Conversas</h3>
                  <div className="flex gap-3">
                    {['boa','media','ruim'].map(q=>(
                      <div key={q} className="flex-1 text-center">
                        <p className="text-2xl font-bold">{stats.qualidade[q]}</p>
                        <p className="text-xs text-gray-400 capitalize">{q === 'boa' ? '\u{1F7E2} Boas' : q === 'media' ? '\u{1F7E1} Medias' : '\u{1F534} Ruins'}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Intencoes Detectadas</h3>
                  <BarChart data={Object.entries(stats.intents || {}).map(([k,v])=>({intent:k,count:v}))} labelKey="intent" valueKey="count" />
                </div>
              </div>

              {/* Daily Trend */}
              {stats.daily_trend && stats.daily_trend.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Tendencia Diaria</h3>
                  <div className="flex items-end gap-1 h-32">
                    {stats.daily_trend.map((d, i) => {
                      const maxT = Math.max(...stats.daily_trend.map(x => x.total));
                      const h = maxT > 0 ? (d.total / maxT * 100) : 0;
                      const errH = d.total > 0 ? (d.erros / d.total * 100) : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.total} msgs, ${d.erros} erros`}>
                          <div className="w-full relative rounded-t" style={{ height: `${h}%`, minHeight: '4px' }}>
                            <div className="absolute bottom-0 w-full bg-orange-500/80 rounded-t" style={{ height: '100%' }} />
                            <div className="absolute bottom-0 w-full bg-red-500 rounded-t" style={{ height: `${errH}%` }} />
                          </div>
                          <span className="text-[9px] text-gray-500">{d.date.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-2 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded" />Total</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded" />Erros</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CONVERSAS TAB */}


          {/* ALERTAS TAB (P1) */}
          {tab === 'alertas' && (
            <div className="space-y-6" data-testid="alerts-tab">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-red-950/60 border border-red-800 rounded-xl p-4">
                  <p className="text-xs text-red-300 uppercase tracking-wide">Tickets HIGH abertos</p>
                  <p className="text-3xl font-bold text-red-200 mt-1">{activeAlerts.counts.tickets_high}</p>
                </div>
                <div className="bg-yellow-950/60 border border-yellow-800 rounded-xl p-4">
                  <p className="text-xs text-yellow-300 uppercase tracking-wide">Tickets abertos (total)</p>
                  <p className="text-3xl font-bold text-yellow-200 mt-1">{activeAlerts.counts.tickets_open}</p>
                </div>
                <div className="bg-orange-950/60 border border-orange-800 rounded-xl p-4">
                  <p className="text-xs text-orange-300 uppercase tracking-wide">Fila humana (HUMAN_QUEUE)</p>
                  <p className="text-3xl font-bold text-orange-200 mt-1">{activeAlerts.counts.queue_open}</p>
                </div>
              </div>

              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-300">Tickets SAC / Parceria / Handoff</h3>
                  <span className="text-xs text-gray-500">Auto-atualiza 30s</span>
                </div>
                {activeAlerts.tickets.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6">Sem tickets abertos no momento.</p>
                ) : (
                  <div className="space-y-2 max-h-[520px] overflow-y-auto">
                    {activeAlerts.tickets.map(t => {
                      const colorMap = { SAC:'border-red-600 bg-red-950/30', PARCERIA:'border-blue-600 bg-blue-950/30', URGENT_HUMAN:'border-orange-600 bg-orange-950/30', LOOP:'border-purple-600 bg-purple-950/30' };
                      const borderCls = colorMap[t.trigger_type] || 'border-gray-700 bg-gray-900/40';
                      const prioColor = t.priority === 'HIGH' ? 'bg-red-500 text-white' : t.priority === 'MEDIUM' ? 'bg-yellow-500 text-black' : 'bg-gray-500 text-white';
                      return (
                        <div key={t.ticket_id} className={'border rounded-lg p-3 text-xs ' + borderCls} data-testid={'ticket-' + t.ticket_id}>
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={'px-2 py-0.5 rounded text-[10px] font-bold ' + prioColor}>{t.priority}</span>
                                <span className="text-gray-200 font-medium">{t.trigger_type}</span>
                                <span className="text-gray-400">&middot; {t.contact_name || t.contact_jid}</span>
                              </div>
                              <p className="text-gray-400 mt-1">wa.me/{t.contact_jid}{t.last_order_number ? (' · Pedido #' + t.last_order_number) : ''}</p>
                            </div>
                            <span className="text-[10px] text-gray-500 shrink-0">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR',{timeZone:'America/Sao_Paulo'}) : ''}</span>
                          </div>
                          <p className="text-gray-200 italic">&quot;{t.customer_message}&quot;</p>
                          <p className="text-gray-500 mt-1">Gatilho: <span className="font-mono text-gray-300">{t.trigger_word}</span></p>
                          <div className="flex gap-2 mt-2">
                            <button data-testid={'assign-ticket-btn-' + t.ticket_id} onClick={async()=>{try{await api.post('/monitoring/tickets/' + t.ticket_id + '/assign');fetchData();}catch(e){alert('Erro ao atribuir');}}} className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-[11px]">Assumir</button>
                            <button data-testid={'resolve-ticket-btn-' + t.ticket_id} onClick={async()=>{const note=prompt('Nota da resolucao (opcional):')||'';try{await api.post('/monitoring/tickets/' + t.ticket_id + '/resolve',{resolution_note:note,release_bot:true});fetchData();}catch(e){alert('Erro ao resolver');}}} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px]">Resolver + Liberar bot</button>
                            <a href={'https://wa.me/' + t.contact_jid} target="_blank" rel="noreferrer" className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-[11px]">Abrir WhatsApp</a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {activeAlerts.queue.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Conversas na fila humana (HUMAN_QUEUE)</h3>
                  <div className="space-y-2">
                    {activeAlerts.queue.map(q => (
                      <div key={q.conversation_id} className="border border-orange-800/50 bg-orange-950/20 rounded-lg p-2 text-xs" data-testid={'queue-item-' + q.conversation_id}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-orange-300 font-medium">{q.contact_jid}</span>
                            <span className="text-gray-500 ml-2">motivo: {q.handoff_reason || '-'}</span>
                          </div>
                          <span className="text-[10px] text-gray-500">{q.last_message_at ? new Date(q.last_message_at).toLocaleString('pt-BR',{timeZone:'America/Sao_Paulo'}) : ''}</span>
                        </div>
                        <p className="text-gray-300 mt-1">&quot;{q.last_incoming}&quot;</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {tab === 'conversas' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="search-conversations"
                  type="text" placeholder="Buscar por nome ou telefone..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                <select
                  data-testid="quality-filter"
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                  value={qualityFilter} onChange={e => { setQualityFilter(e.target.value); setPage(1); }}
                >
                  <option value="">Todas qualidades</option>
                  <option value="BOA">Boas</option>
                  <option value="MEDIA">Medias</option>
                  <option value="RUIM">Ruins</option>
                </select>
              </div>

              <p className="text-xs text-gray-400">{totalConvs} conversas encontradas</p>

              <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-left text-xs text-gray-400 uppercase">
                      <th className="px-4 py-3">Cliente</th>
                      <th className="px-4 py-3">Msgs</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Qualidade</th>
                      <th className="px-4 py-3">Erros</th>
                      <th className="px-4 py-3">Ultima msg</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversations.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Nenhuma conversa encontrada no periodo.</td></tr>
                    ) : conversations.map((c, i) => (
                      <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition">
                        <td className="px-4 py-3 font-medium text-white">{c.name}</td>
                        <td className="px-4 py-3 text-gray-300">{c.total_messages}</td>
                        <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                        <td className="px-4 py-3"><QualityBadge quality={c.quality} /></td>
                        <td className="px-4 py-3 text-gray-300">{c.errors}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{c.last_message_at ? new Date(c.last_message_at).toLocaleString('pt-BR',{timeZone:'America/Sao_Paulo'}) : '-'}</td>
                        <td className="px-4 py-3">
                          <button
                            data-testid={`view-analysis-${c.contact_jid}`}
                            onClick={() => setSelectedJid(c.contact_jid)}
                            className="text-orange-400 hover:text-orange-300 text-xs font-medium"
                          >Ver analise</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalConvs > 30 && (
                <div className="flex justify-center gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-40">Anterior</button>
                  <span className="text-sm text-gray-400 py-1">Pagina {page}</span>
                  <button disabled={page * 30 >= totalConvs} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-40">Proxima</button>
                </div>
              )}
            </div>
          )}

          {/* INSIGHTS TAB */}
          {tab === 'insights' && insights && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Onde o Bot Falha Mais</h3>
                  {insights.top_error_messages && insights.top_error_messages.length > 0 ? (
                    <div className="space-y-2">
                      {insights.top_error_messages.slice(0, 8).map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="text-red-400 font-mono w-6 text-right">{e.count}x</span>
                          <span className="text-gray-300 truncate flex-1">"{e.message}"</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-500 text-sm">Sem dados de erros no periodo</p>}
                </div>

                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Taxa de Falha por Intencao</h3>
                  {insights.intent_failure_rates && insights.intent_failure_rates.length > 0 ? (
                    <div className="space-y-2">
                      {insights.intent_failure_rates.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="w-20 text-gray-400">{f.intent}</span>
                          <div className="flex-1 bg-gray-700 rounded h-3 overflow-hidden">
                            <div className="bg-red-500/70 h-full rounded" style={{ width: `${f.taxa_erro}%` }} />
                          </div>
                          <span className="text-gray-300 w-12 text-right">{f.taxa_erro}%</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-500 text-sm">Sem dados</p>}
                </div>
              </div>

              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Vendas Perdidas (Oportunidades nao convertidas)</h3>
                {insights.lost_sales && insights.lost_sales.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {insights.lost_sales.map((s, i) => (
                      <div key={i} className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-3 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-orange-400 font-medium">{s.name || s.jid}</span>
                          <span className="text-gray-500">{s.timestamp ? new Date(s.timestamp).toLocaleString('pt-BR',{timeZone:'America/Sao_Paulo'}) : ''}</span>
                        </div>
                        <p className="text-gray-300">Cliente: "{s.message}"</p>
                        <p className="text-gray-500">Bot: "{(s.bot_response||'').slice(0,100)}"</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-gray-500 text-sm">Nenhuma venda perdida detectada no periodo</p>}
              </div>

              {insights.hourly_distribution && insights.hourly_distribution.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Distribuicao por Horario</h3>
                  <div className="flex items-end gap-0.5 h-24">
                    {Array.from({length: 24}, (_, h) => {
                      const d = insights.hourly_distribution.find(x => x.hour === h);
                      const val = d ? d.count : 0;
                      const maxV = Math.max(...insights.hourly_distribution.map(x => x.count));
                      const pct = maxV > 0 ? (val / maxV * 100) : 0;
                      return (
                        <div key={h} className="flex-1 flex flex-col items-center" title={`${h}h: ${val} msgs`}>
                          <div className="w-full bg-orange-500/70 rounded-t" style={{ height: `${pct}%`, minHeight: val > 0 ? '2px' : '0' }} />
                          {h % 3 === 0 && <span className="text-[8px] text-gray-500 mt-0.5">{h}h</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedJid && (
        <ConversationDetail jid={selectedJid} days={days} onClose={() => setSelectedJid(null)} />
      )}
    </div>

    </MainLayout>

  );
}
